import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, BehaviorSubject, map, catchError, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { GMSearchResponse, GMSearchResult, User } from '@org/interfaces';


@Injectable({
  providedIn: 'root'
})
export class UserService {
  private apiUrl = `${environment.apiUrl}/api/users`;
  private userSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public user$: Observable<any> = this.userSubject.asObservable();
  private userUpdateBC$: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public userUpdBR$: Observable<any> = this.userUpdateBC$.asObservable();

  constructor(private http: HttpClient) {}

  setUser(user: any) {
    this.userSubject.next(user);
  }

  getUserValue(): any {
    return this.userSubject.value;
  }

  register(user: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/register`, user);
  }

  login(credentials: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/login`, credentials);
  }

  getUser(): Observable<User> {
    return this.http.get<User>(`${this.apiUrl}/me`);
  }

  getUserById(id: string): Observable<User> {
    return this.http.get<User>(`${this.apiUrl}/${id}`);
  }

 
  getUsersByTenant(tid: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/tenant-users/${tid}`);
  }

  updateUser2(user: User): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/me`, user);
  }

  updateUser(user: User): Observable<User> {
     // Ensure roles are correctly formatted before sending to the server 
   // const updatedUser = { ...user, roles: user.roles.map(role => role._id) };
   return this.http.put<User>(`${this.apiUrl}/me/${user._id?.toString()}`, user);
  }

  updateProfile(userId: string, profileData: { name?: string; email?: string }): Observable<any> {
    return this.http.patch(`${this.apiUrl}/profile/${userId}`, profileData).pipe(
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error); 
      })
    );
  }

  deleteUser(): Observable<User> {
    return this.http.delete<any>(`${this.apiUrl}/me`);
  }

  changePassword(password: object): Observable<string> {
    return this.http.post<string>(`${this.apiUrl}/me/change-password`, password);
  }

  resetPassword(userId: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/${userId}/reset-password`, {});
  }

  getRoles(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/getroles`).pipe(
      map(roles => roles.filter(role => role.name !== 'developer'))
    );
  }

  getAssignableRoles(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/assignable-roles`);
  }

  changeRole(data: User): Observable<User> {
    return this.http.post<User>(`${this.apiUrl}/change-role`, data);
  }

   // Function to generate initials
  getInitials(name: string): string {
    if (!name) {
      return '';
    }
    const names = name.split(' ');
    const initials = names.map(n => n.charAt(0).toUpperCase()).join('');
    return initials.slice(0, 2); // Return only the first two initials
  }

  // Function to generate background color based on initials
  getBackgroundColor(initials: string): string {
    if (!initials) {
      return 'bg-gray-500'; // default color
    }
    const colors = [
      'bg-red-500', 'bg-green-500', 'bg-blue-500', 'bg-yellow-500',
      'bg-purple-500', 'bg-pink-500', 'bg-indigo-500', 'bg-gray-500'
    ];
    const charCodeSum = initials
      .split('')
      .reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[charCodeSum % colors.length];
  }

  setBR(data: any) {
    this.userUpdateBC$.next(data);
  }

  subscribeToNotifications(subscription: PushSubscription): Observable<any> {
    const userId = this.getUserValue()._id;  // Assuming you have the user ID available here
    return this.http.post<any>(`${environment.apiUrl}/api/notifications/subscribe`, { userId, token: subscription });
  }

  
  searchGMs(term: string): Observable<GMSearchResult[]> {
    return this.http.get<GMSearchResponse>(`${this.apiUrl}/search-gms?searchTerm=${encodeURIComponent(term)}`)
      .pipe(
        map(response => response.data),
        catchError(error => {
          console.error('Error searching GMs:', error);
          return throwError(() => new Error('Failed to search GMs'));
        })
      );
  }

}
